'use client';

/* eslint-disable @typescript-eslint/no-unused-vars */
import useResizeObserver from '@react-hook/resize-observer';
import { type RefObject, useLayoutEffect, useState } from 'react';

// Type definition for the useSize hook target.

type Target = RefObject<HTMLDivElement | null>;

/**
 * Custom hook to easily get an element's DOMRect using ResizeObserver.
 *
 * @param target - A React ref.
 *
 * @returns The element's DOMRect.
 *
 * @see https://github.com/jaredLunde/react-hook/tree/master/packages/resize-observer#readme
 */

export const useSize = (target: Target) => {
  const [size, setSize] = useState<DOMRectReadOnly>();

  useLayoutEffect(() => {
    if (target.current) {
      setSize(target.current.getBoundingClientRect());
    }
  }, [target]);

  useResizeObserver(
    target,
    (entry) => setSize(entry.target.getBoundingClientRect()),
    useResizeObserverSSROptions
  );

  return size;
};

/**
 * Polyfill for the ResizeObserver on the server side.
 *
 * @see https://github.com/jaredLunde/react-hook/issues/314
 */

class SSRResizeObserverPolyfill {
  constructor(_callback: ResizeObserverCallback) {}
  observe(target: Element, options?: ResizeObserverOptions) {}
  unobserve(target: Element) {}
}

/**
 * SSR options for useResizeObserver hook.
 */

export const useResizeObserverSSROptions = {
  polyfill: typeof window === 'undefined' ? SSRResizeObserverPolyfill : undefined,
};
